import SweetScroll from 'sweet-scroll';
import Validator from '@/js/modules/Validator';
import { detectLocaleByHtml } from '@/js/utils/browser';

// Schema
import schema, { MESSAGES } from '@/js/data/schema';
import { file } from 'zod-form-data';

export default class {
  constructor (selector) {
    const { locale } = detectLocaleByHtml();

    this.selector = selector;
    this.el = document.querySelector(selector);
    this.headerEl = document.querySelector('.fd-l-header'); // NOTE: selectorを付与した方がよいがDOM変更せずに適用
    if (!this.el) {
      return;
    }
    this.textarea = [...this.el.querySelectorAll('textarea')];

    this.uploadArea = this.el.querySelector('.js-file-upload');
    this.uploadList = this.el.querySelector('.js-file-upload-list');
    this.uploadButton = this.el.querySelector('.js-file-upload-button'); // NOTE: ファイル選択ボタン && input[type="file"]
    this.allFilesList = []; // アップロードされたファイルを保持する配列

    this.config = {
      fieldRootSelector: '[data-field-id]'
    };

    this.scroller = new SweetScroll({
      duration: 800,
      easing: 'easeOutQuint'
    });

    /**
     * @doc タイプごとのフィールドセット
     * all すべてのフィールド
     */
    this.showFieldformats = {
      foundation: ['entity_name', 'email', 'contact_body', 'file_upload', '1']
    };

    /**
     * @doc ファイルバリデーション設定
     */
    this.FILE_VALIDATION_CONFIG = {
      // PDfと, Excel(.xlsx)を許可
      allowedTypes: ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      maxTotalSize: 30 * 1024 * 1024, // 30MB
      errorMessages: {
        invalidType: MESSAGES.INCORRECT_FORMAT.file_upload,
        maxTotalSize: MESSAGES.MAX_FILE_SIZE.file_upload
      }
    };

    if (!this.el) {
      return;
    }

    this._bind();

    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', this._initialize);
    } else {
      this._initialize();
    }
  }

  _bind () {
    this._onSubmit = this._onSubmit.bind(this);
    this._initialize = this._initialize.bind(this);
    this._changeAgreementState = this._changeAgreementState.bind(this);
    this._updateSchema = this._updateSchema.bind(this);
    this._updateFormType = this._updateFormType.bind(this);
    // this._onChangeType = this._onChangeType.bind(this);
    this._dropHandler = this._dropHandler.bind(this);
    this._fileSelectHandler = this._fileSelectHandler.bind(this);
    this._updateInputFiles = this._updateInputFiles.bind(this);
    this._renderFileList = this._renderFileList.bind(this);
    this._createFileListItem = this._createFileListItem.bind(this);
    this._removeFileByIndex = this._removeFileByIndex.bind(this);
    this._clearFileList = this._clearFileList.bind(this);
    this._validateFiles = this._validateFiles.bind(this);
    this._displayFileErrors = this._displayFileErrors.bind(this);
  }

  async _initialize () {
    const submitButton = document.querySelector('[type="submit"]');

    this.agreementCheckField = document.querySelector('.js-form-agree');
    this.agreementCheckBox = this.agreementCheckField.querySelector('input[type="checkbox"]');
    this.agreementCheckFieldError = this.agreementCheckField.querySelector('[data-field-error]');
    this.agreementCheckFieldError.innerText = '';
    this.submitButton = submitButton;

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this._onSubmit);
    }

    // ファイルアップロード
    if (this.uploadArea) {
      // ドラッグオーバー時のイベント
      this.uploadArea.addEventListener('dragover', event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy'; // ドロップ時の効果
        this.uploadArea.classList.add('is-dragover'); // ドラッグオーバー中のスタイル追加用class
      });

      // ドラッグアウト
      this.uploadArea.addEventListener('dragleave', () => {
        this.uploadArea.classList.remove('is-dragover'); // ドラッグアウト時にクラスを削除
      });

      // ドロップでアップロード
      this.uploadArea.addEventListener('drop', this._dropHandler);

      // ファイル選択ボタンにてアップロード
      this.uploadButton.addEventListener('change', this._fileSelectHandler);
    }

    this.showFields = [...document.querySelectorAll(`${this.config.fieldRootSelector}:has([data-field])`)];
    this.typeSelect = document.querySelector('[name="contact_type"]');

    this.agreementCheckBox.addEventListener('change', this._changeAgreementState);
    // this.typeSelect.addEventListener('change', this._onChangeType);

    const schemaId = this._updateFormType();

    this.validator = new Validator(this.selector, {
      schema: schema[schemaId],
      options: {
        realtime: true,
        errorToScrollSelector: '.fd-o-form',
        on: {
          ready: state => {
            if (state.errors.length) {
              return;
            }
            if (submitButton.checked) {
              submitButton.checked = false;
            }
          },
          validate: (state, event) => {
            if (!submitButton) {
              return;
            }
            const hasError = Boolean(state.errors.length);
            const isChecked = this.agreementCheckBox.checked;
            const isInvalid = hasError || !isChecked;

            if (!state.ready) {
              return;
            }

            if (isInvalid) {
              document.documentElement.classList.add('has-validate-error');
            } else {
              document.documentElement.classList.remove('has-validate-error');
            }
            submitButton.disabled = isInvalid;

            // 各フィールドの状態を確認
            ['entity_name', 'email', 'contact_body'].forEach(fieldName => {
              const fieldEl = document.querySelector(`[data-field-id="${fieldName}"]`);
              const errorEl = fieldEl?.querySelector('[data-field-error]');
              const hasError = state.errors.some(err => err && err.path && err.path[0] === fieldName);

              // hasError: false かつ errorMessage が残っている場合、エラーメッセージをクリア
              if (!hasError && errorEl && errorEl.innerText.trim() !== '') {
                errorEl.innerText = '';
              }
            });
          }
        }
      }
    });

    await this._updateSchema(schemaId);
  }

  _changeAgreementState (event) {
    const isTrusty = event.target.checked;
    if (isTrusty) {
      this.agreementCheckField.classList.remove('has-error');
      this.agreementCheckFieldError.innerText = '';
      this.agreementCheckBox.setAttribute('aria-invalid', 'false');
    } else {
      this.agreementCheckField.classList.add('has-error');
      this.agreementCheckFieldError.innerText = MESSAGES.MUST_BE_TRUE.privacy;
      this.agreementCheckBox.setAttribute('aria-invalid', 'true');
    }
  }

  _updateFormType () {
    // const typeField = document.querySelector('[name="contact_type"]');
    // const selectedValue = typeField.options[typeField.selectedIndex || 0].value;
    const currentType = 'foundation';

    this.el.setAttribute('data-form-id', currentType);

    return currentType;
  }

  async _updateSchema (schemaId) {
    const fieldFormat = this.showFieldformats[schemaId];
    const newSchema = schema[schemaId];

    // 表示するフィールドを条件により切り替え
    this.showFields.forEach(fieldRoot => {
      const isExists = fieldFormat.includes(fieldRoot.dataset.fieldId);
      if (fieldRoot) {
        if (isExists) {
          fieldRoot.style.display = '';
        } else {
          fieldRoot.style.display = 'none';
        }
      }
    });

    // バリデーションのスキーマを更新
    await this.validator.updateSchema(this.el, newSchema);
  }

  async _onSubmit (event) {
    event.preventDefault();
    // フォーム全体のバリデーションを実施
    const errors = await this.validator.validate(this.el);
    if (errors.length > 0) {
      // エラーがある場合は、エラーメッセージの表示などをそのままにして終了
      return;
    }
    if (this.submitButton.disabled) {
      return;
    }
    // 送信直前に最新のreCAPTCHAトークンを取得（timeout-or-duplicate防止）
    this.submitButton.disabled = true;
    try {
      const sitekey = this.el.dataset.recaptchaSitekey;
      if (sitekey) {
        if (typeof grecaptcha === 'undefined') {
          console.warn('reCAPTCHA script is not loaded, skipping token refresh.');
        } else {
          await new Promise((resolve, reject) => {
            grecaptcha.ready(() => {
              grecaptcha.execute(sitekey, { action: 'submit' })
                .then((token) => {
                  const tokenField = document.getElementById('recaptchaToken');
                  if (tokenField) tokenField.value = token;
                  resolve();
                })
                .catch(reject);
            });
          });
        }
      }
      // 送信
      this.el.submit();
    } catch (err) {
      console.error('Form submission error:', err);
      this.submitButton.disabled = false;
    }
  }
  /**
   * @doc CSSのサポート状況を鑑みpolyfillを同時に適用
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/field-sizing
   */
  setTextareaHeight (textarea) {
    const adjustHeight = () => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    };
    adjustHeight();
    textarea.addEventListener('input', adjustHeight);
    window.addEventListener('resize', adjustHeight);
  }

  removeTextareaHeight (textarea) {
    textarea.style.height = '';
  }

  /**
   * @doc ファイル添付エリア
   * ドラッグ&ドロップでの添付と、「ファイル選択」ボタンでの添付をサポート
   * アップロードされたファイルは、リスト形式で表示
   * ゴミ箱マークをクリックすると、リストから削除される
   * 添付はPDFとExcel(.xlsx)のみ、10MB以下の制限を設ける(schemaはプレーンテキストのみのため、このjsで制御)
   **/

  /**
   * input[type="file"]のFileListを更新
   * @param {Array} fileArray - Fileオブジェクトの配列
   * @param {boolean} append - 既存のファイルに追加するかどうか (デフォルト: false)
   */
  _updateInputFiles (fileArray, append = false) {
    const dataTransfer = new DataTransfer();

    if (append) {
      // 現在のファイルを保持
      this.allFilesList.forEach(file => dataTransfer.items.add(file));
      // 新しいファイルを追加
      fileArray.forEach(file => {
        dataTransfer.items.add(file);
        this.allFilesList.push(file); // 全ファイルリストに追加
      });
    } else {
      // 新しいファイルのみを設定
      this.allFilesList = [...fileArray];
      fileArray.forEach(file => dataTransfer.items.add(file));
    }

    this.uploadButton.files = dataTransfer.files;
  }

  /**
   * ファイルリストのDOMを生成・更新
   * @param {Array} files - Fileオブジェクトの配列
   */
  _renderFileList (files) {
    if (files.length === 0) {
      this._clearFileList();
      return;
    }

    this.uploadArea.classList.remove('is-empty'); // 空状態のstyleを解除
    this.uploadList.innerHTML = ''; // 既存のリストをクリア

    files.forEach((file, index) => {
      const ListItem = this._createFileListItem(file, index);
      this.uploadList.appendChild(ListItem);
    });
  }

  /**
   * 単一のファイルリストアイテムDOMを生成
   * @param {File} file - Fileオブジェクト
   * @param {number} index - ファイルのインデックス
   * @returns {HTMLLIElement} 生成されたリストアイテム
   */
  _createFileListItem (file, index) {
    // リストアイテムの生成
    const listItem = document.createElement('li');
    listItem.className = 'fd-c-file-upload__button';
    listItem.textContent = file.name;

    // 削除ボタンの生成
    const deleteButton = document.createElement('button');
    deleteButton.classList.add('fd-c-file-upload__button__delete');
    deleteButton.setAttribute('aria-label', 'ファイルを削除');
    deleteButton.addEventListener('click', () => {
      this._removeFileByIndex(index);
    });
    listItem.appendChild(deleteButton);
    return listItem;
  }

  /**
   * 指定されたインデックスのファイルを削除
   * @param {number} index - 削除するファイルのインデックス
   */
  _removeFileByIndex (index) {
    this.allFilesList.splice(index, 1); // 指定されたインデックスのファイルを削除

    if (this.allFilesList.length === 0) {
      // ファイルが0件になった場合はクリア処理
      this._clearFileList();
    } else {
      // 削除後のファイルリストをバリデーション
      const errors = this._validateFiles(this.allFilesList);
      this._displayFileErrors(errors);

      this._updateInputFiles(this.allFilesList); // ファイルリストを更新
      this._renderFileList(this.allFilesList); // リストを再描画
    }
  }

  /**
   * ファイルリストの内容をクリア
   */
  _clearFileList () {
    this.allFilesList = []; // 全ファイルリストをクリア
    this.uploadArea.classList.add('is-empty'); // 空状態のstyleを適用
    this.uploadList.innerHTML = ''; // リストをクリア
    this._updateInputFiles([]); // input[type="file"]のファイルをクリア
    this._displayFileErrors([]); // エラーもクリア
  }

  /**
   * ファイルのバリデーションを実行
   * @param {FileList} files - バリデーション対象のファイル
   * @returns {Array} エラーメッセージの配列
   */
  _validateFiles (files) {
    // Setを使用して重複を排除
    const errors = new Set();
    let totalSize = 0;

    files.forEach(file => {
      totalSize += file.size;

      // PDFとExcel(.xlsx)のみ許可
      if (!this.FILE_VALIDATION_CONFIG.allowedTypes.includes(file.type)) {
        errors.add(this.FILE_VALIDATION_CONFIG.errorMessages.invalidType);
      }
    });

    // 合計サイズチェック
    if (totalSize > this.FILE_VALIDATION_CONFIG.maxTotalSize) {
      const totalSizeMB = (totalSize / 1024 / 1024).toFixed(1);
      errors.add(`${this.FILE_VALIDATION_CONFIG.errorMessages.maxTotalSize}（現在: ${totalSizeMB}MB）`);
    }

    // Setを配列に変換して返す
    return Array.from(errors);
  }

  /**
   * ファイルエラーメッセージを表示/非表示
   * @param {Array} errors - エラーメッセージの配列
   */
  _displayFileErrors (errors) {
    const fileUploadField = document.querySelector('[data-field-id="file_upload"]');
    const errorElement = fileUploadField?.querySelector('[data-field-error]');

    if (!errorElement) return;

    if (errors.length > 0) {
      fileUploadField.classList.add('has-error');
      errorElement.innerHTML = errors.join('<br>');
    } else {
      fileUploadField.classList.remove('has-error');
      errorElement.innerHTML = '';
    }
  }

  // ファイルがドロップされたとき
  _dropHandler (event) {
    event.preventDefault();
    this.uploadArea.classList.remove('is-dragover');

    const uploadFiles = event.dataTransfer.files;
    const uploadFilesArray = Array.from(uploadFiles);

    if (uploadFilesArray.length > 0) {
      const allFiles = [...this.allFilesList, ...uploadFilesArray];
      // バリデーション実行
      const errors = this._validateFiles(allFiles);
      this._displayFileErrors(errors);

      if (errors.length === 0) {
        // エラーがない場合のみリストに追加
        this._updateInputFiles(uploadFilesArray, true); // 既存のファイルに追加
        this._renderFileList(this.allFilesList);
      }
    } else {
      this._clearFileList();
    }
  }

  // ファイルが選択されたとき
  _fileSelectHandler (event) {
    const uploadFiles = event.target.files;
    const uploadFilesArray = Array.from(uploadFiles);

    this.uploadArea.classList.remove('is-dragover');

    if (uploadFilesArray.length > 0) {
      const allFiles = [...this.allFilesList, ...uploadFilesArray];
      // バリデーション実行
      const errors = this._validateFiles(allFiles);
      this._displayFileErrors(errors);

      if (errors.length === 0) {
        // エラーがない場合のみリストに追加
        this._updateInputFiles(uploadFilesArray, true); // 既存のファイルに追加
        this._renderFileList(this.allFilesList);
      } else {
        // エラーがある場合はinput要素をクリア
        this.uploadButton.value = '';
      }
    }
  }
}
